import { marked } from 'marked'
import { useEffect, useRef, type ReactNode } from 'react'
import type { ContentPlugin } from './types'
import type { DocRef } from '../lib/documentViewer'
import { openDocument } from '../lib/documentViewer'
import { api } from '../api/client'
import { sanitizeHtml } from '../lib/sanitizeHtml'
import { copyText } from '../lib/clipboard'
import { extractImageMarkers, extractFileMarkers, ImageThumbnails, FileBadges } from './imageAttach'
import { extractInlineDocs, detectFileDocs, DocAttachments } from './docAttach'

// Configure marked once
marked.setOptions({ breaks: true, gfm: true })

// Escape HTML so raw code/diff text can never break out of its container (XSS-safe).
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

// Escape for use in HTML attribute values (double-quoted).
function escAttr(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

// ─── Handlers for md links inside dangerouslySetInnerHTML ────────────────────────
// Buttons carry data-md-link + data-src / data-kind / data-chatid. They no longer
// carry inline on* handlers (those are stripped by sanitizeHtml and would break the
// markup anyway); instead a single delegated listener per .markdown-content container
// (see MarkdownContent) calls these on hover/click.
// Hover: resolve once via statDoc → pin absolute src or store candidates.
// Click: open (single) or show picker (multi-candidate).
let _activePicker: HTMLElement | null = null

function _showPicker(anchor: HTMLElement, candidates: string[], chatId?: string) {
  _activePicker?.remove()
  const picker = document.createElement('div')
  picker.className = 'md-picker'
  candidates.forEach(abs => {
    const item = document.createElement('button')
    item.className = 'md-picker-item'
    item.textContent = abs
    item.onclick = (e) => {
      e.stopPropagation()
      picker.remove(); _activePicker = null
      openDocument({ kind: 'file', name: abs.split('/').pop() || abs, src: abs, chatId })
    }
    picker.appendChild(item)
  })
  anchor.appendChild(picker)
  _activePicker = picker
  const close = (e: MouseEvent) => {
    if (!picker.contains(e.target as Node)) {
      picker.remove(); _activePicker = null
      document.removeEventListener('click', close, true)
    }
  }
  setTimeout(() => document.addEventListener('click', close, true), 0)
}

async function _mdEnter(btn: HTMLElement) {
  if (btn.dataset['resolved']) return
  btn.dataset['resolved'] = '1'
  const src    = btn.dataset['src']    || ''
  const chatId = btn.dataset['chatid'] || ''
  if (!src || !chatId) return
  try {
    const { exists, path: resolved, candidates } = await api.chats.statDoc(chatId, src)
    if (candidates && candidates.length > 1) {
      btn.dataset['candidates'] = JSON.stringify(candidates)
      btn.dataset['mdpath'] = `${candidates.length} ไฟล์ — คลิกเพื่อเลือก`
    } else if (resolved) {
      btn.dataset['src'] = resolved
      btn.dataset['mdpath'] = resolved
    } else {
      btn.dataset['mdpath'] = exists ? src : `ไม่เจอ: ${src}`
    }
  } catch { /* leave as-is */ }
}

async function _mdClick(btn: HTMLElement) {
  const kind   = (btn.dataset['kind'] || 'file') as 'file' | 'url'
  const chatId = btn.dataset['chatid'] || undefined

  if (kind === 'url') {
    const src = btn.dataset['src'] || ''
    if (src) openDocument({ kind, name: src.split('/').pop() || src, src, chatId })
    return
  }

  // Multi-candidate already resolved by hover
  const candidatesJson = btn.dataset['candidates']
  if (candidatesJson) {
    try { _showPicker(btn, JSON.parse(candidatesJson), chatId) } catch {}
    return
  }

  const src = btn.dataset['src'] || ''

  // Already absolute — open directly
  if (src.startsWith('/')) {
    openDocument({ kind: 'file', name: src.split('/').pop() || src, src, chatId })
    return
  }

  // Not yet resolved — resolve now (user clicked without hovering first)
  if (chatId) {
    try {
      const { path: resolved, candidates } = await api.chats.statDoc(chatId, src)
      if (candidates && candidates.length > 1) {
        btn.dataset['candidates'] = JSON.stringify(candidates)
        _showPicker(btn, candidates, chatId)
        return
      }
      if (resolved) {
        btn.dataset['src'] = resolved
        openDocument({ kind: 'file', name: resolved.split('/').pop() || src, src: resolved, chatId })
        return
      }
    } catch { /* fall through */ }
  }

  // Fallback: let DocumentPanel show the error
  if (src) openDocument({ kind: 'file', name: src.split('/').pop() || src, src, chatId })
}

// ─── Rendered-markdown container ─────────────────────────────────────────────────
// Sanitizes the marked() HTML and hosts ONE delegated listener per container,
// replacing the per-element inline on* handlers we used to emit (those break under
// HTML sanitization). md-link buttons carry data-md-link; copy buttons data-copy.
export function MarkdownContent(
  { html, className }: { html: string; className?: string },
) {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const root = ref.current
    if (!root) return
    // mouseover (bubbles) substitutes for the non-bubbling mouseenter; _mdEnter is
    // idempotent (guards on data-resolved), so repeated fires over the same button
    // are harmless.
    const onOver = (e: Event) => {
      const btn = (e.target as Element)?.closest?.('[data-md-link]') as HTMLElement | null
      if (btn && root.contains(btn)) void _mdEnter(btn)
    }
    const onClick = (e: Event) => {
      const el = e.target as Element
      const link = el?.closest?.('[data-md-link]') as HTMLElement | null
      if (link && root.contains(link)) { void _mdClick(link); return }
      const copy = el?.closest?.('[data-copy]') as HTMLElement | null
      if (copy && root.contains(copy)) {
        const code = copy.closest('.code-block-wrapper')?.querySelector('code')
        if (code) {
          void copyText(code.textContent || '').then(ok => {
            if (!ok) return
            copy.textContent = 'Copied ✓'
            copy.classList.add('copied')
            setTimeout(() => {
              copy.textContent = 'Copy'
              copy.classList.remove('copied')
            }, 1500)
          })
        }
      }
    }
    root.addEventListener('mouseover', onOver)
    root.addEventListener('click', onClick)
    return () => {
      root.removeEventListener('mouseover', onOver)
      root.removeEventListener('click', onClick)
    }
  }, [html])

  return (
    <div
      ref={ref}
      className={className ? `markdown-content ${className}` : 'markdown-content'}
      dangerouslySetInnerHTML={{ __html: sanitizeHtml(html) }}
    />
  )
}

// ─── Renderer factory (per-render, carries chatId in closures) ────────────────
function buildRenderer(chatId?: string) {
  const renderer = new marked.Renderer()

  // Inline code span: `path.md` → clickable button wrapping <code>
  ;(renderer as any).codespan = ({ text }: { text: string }): string => {
    if (/\.md$/i.test(text.trim())) {
      const cid = chatId ? ` data-chatid="${escAttr(chatId)}"` : ''
      return `<button class="md-link" data-md-link data-src="${escAttr(text.trim())}" data-kind="file" data-mdpath="${escAttr(text.trim())}"${cid}><code>${escapeHtml(text)}</code></button>`
    }
    return `<code>${escapeHtml(text)}</code>`
  }

  // Link: intercept whimer-doc: protocol injected by injectProseLinks()
  const origLink = (renderer as any).link.bind(renderer)
  ;(renderer as any).link = (token: any): string => {
    if (token.href?.startsWith('whimer-doc:')) {
      const src  = token.href.slice('whimer-doc:'.length)
      const kind = /^https?:\/\//.test(src) ? 'url' : 'file'
      const cid  = chatId ? ` data-chatid="${escAttr(chatId)}"` : ''
      return `<button class="md-prose-link" data-md-link data-src="${escAttr(src)}" data-kind="${kind}" data-mdpath="${escAttr(src)}"${cid}>${escapeHtml(src)}</button>`
    }
    return origLink(token)
  }

  return renderer
}

// ─── Pre-process markdown: wrap detected prose .md refs as markdown links ─────
// Splits on inline code spans + fenced blocks so we never touch code content.
function injectProseLinks(content: string, refs: DocRef[]): string {
  if (refs.length === 0) return content
  const parts: string[] = []
  let last = 0
  const splitRe = /(```[\s\S]*?```|`[^`]+`)/g
  let m: RegExpExecArray | null

  while ((m = splitRe.exec(content)) !== null) {
    let prose = content.slice(last, m.index)
    for (const ref of refs) {
      if (!ref.src) continue
      const esc = ref.src.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      prose = prose.replace(new RegExp(esc, 'g'), `[${ref.src}](whimer-doc:${ref.src})`)
    }
    parts.push(prose, m[0])
    last = m.index + m[0].length
  }

  let tail = content.slice(last)
  for (const ref of refs) {
    if (!ref.src) continue
    const esc = ref.src.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    tail = tail.replace(new RegExp(esc, 'g'), `[${ref.src}](whimer-doc:${ref.src})`)
  }
  parts.push(tail)
  return parts.join('')
}

// Render a unified diff as styled rows. Each row = [line number][+/- sign][code].
// Numbers are COMPUTED from the `@@ -a,b +c,d @@` hunk header (deletions advance the old
// side, additions the new side, context both), so they work for ANY agent that emits a real
// unified diff. The noisy plumbing lines (`@@ … @@`, `--- a/…`, `+++ b/…`, `diff`/`index`)
// are NOT rendered — the filename is lifted into the block header instead (see diffFilename).
const HUNK_RE = /^@@\s*-(\d+)(?:,\d+)?\s+\+(\d+)(?:,\d+)?\s*@@/

// Lift the changed file's path out of a diff so it can headline the block instead of the
// raw `+++ b/…` line. Prefer the new-file side (`+++`), fall back to the old (`---`); strip
// the a//b/ prefix and any trailing tab-timestamp. '' if not a file diff.
function diffFilename(text: string): string {
  const pick = (prefix: string, strip: RegExp): string => {
    for (const line of text.split('\n')) {
      if (line.startsWith(prefix)) {
        const p = line.slice(prefix.length).split('\t')[0].trim().replace(strip, '')
        if (p && p !== '/dev/null') return p
      }
    }
    return ''
  }
  return pick('+++ ', /^b\//) || pick('--- ', /^a\//)
}

function renderDiff(text: string): string {
  const lines = text.split('\n')
  let oldLn = 0
  let newLn = 0
  let active = false // inside a hunk → line numbers are known
  const out: string[] = []
  for (const line of lines) {
    // Parse `@@` for the line-number anchor but do not render it (hidden plumbing).
    if (line.startsWith('@@')) {
      const m = line.match(HUNK_RE)
      if (m) { oldLn = parseInt(m[1], 10); newLn = parseInt(m[2], 10); active = true }
      continue
    }
    // Hide the file/index header lines too — the filename moves to the block header.
    if (line.startsWith('+++') || line.startsWith('---') || line.startsWith('diff ') || line.startsWith('index ')) {
      continue
    }
    let cls = 'diff-context'
    let num = ''   // single gutter column: each line shows its own line number
    let sign = ''  // +/- marker in its OWN column so it sits apart from the code
    let body = line
    if (line.startsWith('+')) {
      cls = 'diff-add'; sign = '+'; body = line.slice(1)
      if (active) { num = String(newLn); newLn++ }       // added → new-file line number
    } else if (line.startsWith('-')) {
      cls = 'diff-del'; sign = '-'; body = line.slice(1)
      if (active) { num = String(oldLn); oldLn++ }        // removed → old-file line number
    } else {
      // context — present in both; strip the single leading space, show the new number
      body = line.startsWith(' ') ? line.slice(1) : line
      if (active) { num = String(newLn); oldLn++; newLn++ }
    }
    const safe = escapeHtml(body)
    // Keep empty lines from collapsing so the gutter column stays aligned.
    out.push(`<span class="diff-line ${cls}"><span class="diff-gutter">${num}</span><span class="diff-sign">${sign}</span><span class="diff-text">${safe || '&nbsp;'}</span></span>`)
  }
  return out.join('\n')
}

// ─── Plugin 1: Markdown (fallback, score 1) ───────────────────────────────────

const markdownPlugin: ContentPlugin = {
  name: 'markdown',
  detect: (_content: string) => 1,
  render: (content: string, _onInput?: (text: string) => void, chatId?: string): ReactNode => {
    const __html = marked.parse(content, { renderer: buildRenderer(chatId) }) as string
    return <MarkdownContent html={__html} />
  },
}

// Note: the old v1 frontend prompt detector (interactivePlugin / extractChoices) was removed
// (2026-06-07). Interactive prompts now come ONLY from the backend's DetectedPrompt (real
// screen + exact keystrokes, per-agent R27) rendered by InteractivePromptCard — single source
// of truth. The text-guessing version here sent fixed keys (y\n) and could disagree/false-fire.

// ─── Plugin 2: Code (score 90) ────────────────────────────────────────────────

const codePlugin: ContentPlugin = {
  name: 'code',
  detect: (content: string): number => {
    return content.includes('```') ? 90 : 0
  },
  render: (content: string, _onInput?: (text: string) => void, chatId?: string): ReactNode => {
    const renderer = buildRenderer(chatId)
    renderer.code = ({
      text,
      lang,
    }: {
      text: string
      lang?: string | null
      escaped?: boolean
    }): string => {
      const language = lang || 'text'
      // Diffs get per-line +/- colouring; everything else stays plain escaped text (full
      // syntax highlighting would need a dedicated library).
      const isDiff = language.toLowerCase() === 'diff'
      const inner = isDiff ? renderDiff(text) : escapeHtml(text)
      const codeClass = isDiff ? 'code-block-diff' : ''
      // For a diff, headline the block with the changed FILENAME (lifted out of +++/---)
      // instead of the bare "diff" label; fall back to "diff" when no file path is present.
      const label = isDiff ? (diffFilename(text) || 'diff') : language
      // The label (fence info-string / filename) is agent-controlled and is interpolated into
      // BOTH a double-quoted attribute and element text, so escape <>& AND " (a raw " would
      // break out of data-lang="..." and inject markup under dangerouslySetInnerHTML).
      const langSafe = escapeHtml(label).replace(/"/g, '&quot;')
      return `<div class="code-block-wrapper" data-lang="${langSafe}"><div class="code-lang-label">${langSafe}</div><pre class="code-block"><code class="${codeClass}">${inner}</code></pre><button class="code-copy-btn" data-copy>Copy</button></div>`
    }
    const html = marked.parse(content, { renderer }) as string
    return <MarkdownContent html={html} />
  },
}

// ─── Plugin 3: Table (score 80) ───────────────────────────────────────────────

const tablePlugin: ContentPlugin = {
  name: 'table',
  detect: (content: string): number => {
    const hasTableRow = /^\|.+\|$/m.test(content)
    const hasSeparator = /^\|[\s\-|:]+\|$/m.test(content)
    return hasTableRow && hasSeparator ? 80 : 0
  },
  render: (content: string, _onInput?: (text: string) => void, chatId?: string): ReactNode => {
    const __html = marked.parse(content, { renderer: buildRenderer(chatId) }) as string
    return (
      <div className="table-scroll-wrapper">
        <MarkdownContent html={__html} />
      </div>
    )
  },
}

// ─── Registry & export ────────────────────────────────────────────────────────

const plugins: ContentPlugin[] = [
  markdownPlugin,
  codePlugin,
  tablePlugin,
]

export function renderContent(content: string, onInput?: (text: string) => void, chatId?: string): ReactNode {
  if (!content?.trim()) return null

  // Attachments are orthogonal to content type: pull them OUT first (so the raw path /
  // raw markdown dump never renders), pick the best plugin for whatever text remains,
  // then append the attachment surfaces below it.
  //   • images      → thumbnails (imageAttach) — marker text stays in body (non-user turns)
  //   • inline docs → a ```markdown block becomes a document card, not a code dump (B)
  //   • doc refs    → .md paths / .md URLs mentioned in prose become cards (path/link)
  const { paths: imagePaths } = extractImageMarkers(content)  // paths only — don't strip from body
  const { paths: filePaths, clean: noFiles } = extractFileMarkers(content)
  const { docs: inlineDocs, clean } = extractInlineDocs(noFiles)
  const docRefs = [...inlineDocs, ...detectFileDocs(clean)]

  // Also inject clickable links for prose .md refs directly in the text.
  // Cards (DocAttachments) are kept as-is; this makes the text itself clickable too.
  const proseRefs = docRefs.filter(r => r.kind === 'file' || r.kind === 'url')
  const linked = injectProseLinks(clean, proseRefs)

  let body: ReactNode = null
  if (linked) {
    let best: ContentPlugin = markdownPlugin
    let bestScore = 0
    for (const p of plugins) {
      const score = p.detect(linked)
      if (score > bestScore) {
        bestScore = score
        best = p
      }
    }
    body = best.render(linked, onInput, chatId)
  }

  if (imagePaths.length === 0 && filePaths.length === 0 && docRefs.length === 0) return body
  return (
    <>
      {body}
      {docRefs.length > 0 && <DocAttachments refs={docRefs} chatId={chatId} />}
      {imagePaths.length > 0 && <ImageThumbnails paths={imagePaths} />}
      {filePaths.length > 0 && <FileBadges paths={filePaths} />}
    </>
  )
}
